import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Share2, Copy, Check, ExternalLink, Globe, Lock } from "lucide-react";

export default function SharePage() {
  const [enabled, setEnabled] = useState(false);
  const [slug, setSlug] = useState("");
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/share").then((r) => { setEnabled(r.data.enabled); setSlug(r.data.slug); setLoading(false); });
  }, []); // run once on mount; api + state setters are stable

  const publicUrl = slug ? `${window.location.origin}/g/${slug}` : "";

  const toggle = async () => {
    const next = !enabled;
    const { data } = await api.post("/share", { enabled: next });
    setEnabled(data.enabled);
    setSlug(data.slug);
    toast.success(next ? "Public gallery enabled" : "Gallery is now private");
  };

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="p-6 lg:p-10 max-w-3xl">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-acid">Show Off</div>
        <h1 className="font-display text-5xl lg:text-6xl text-paper leading-none mt-1">SHARE VAULT</h1>
        <p className="font-sans text-sm text-muted-gray mt-3 max-w-lg">
          Turn your collection into a public gallery with a shareable link. Adult (18+) posters stay age-gated for viewers.
        </p>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-muted-gray animate-pulse">Loading...</div>
      ) : (
        <div className="bg-surface border border-stroke p-6 hard-shadow">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {enabled ? <Globe className="text-acid" size={22} /> : <Lock className="text-muted-gray" size={22} />}
              <div>
                <div className="font-display text-2xl text-paper leading-none">{enabled ? "Public" : "Private"}</div>
                <div className="font-mono text-[11px] text-muted-gray mt-1">
                  {enabled ? "Anyone with the link can view your collection" : "Only you can see your collection"}
                </div>
              </div>
            </div>
            <button onClick={toggle} data-testid="share-toggle-button"
              className={`font-mono text-xs uppercase tracking-wider font-bold px-5 py-3 flex items-center gap-2 ${
                enabled ? "border border-stroke text-muted-gray hover:text-paper" : "bg-acid text-ink hover:bg-glow"
              }`}>
              <Share2 size={15} /> {enabled ? "Make Private" : "Make Public"}
            </button>
          </div>

          {enabled && (
            <div className="mt-6 pt-6 border-t border-stroke">
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-gray mb-2">Your public link</div>
              <div className="flex flex-col sm:flex-row gap-2">
                <input readOnly value={publicUrl} data-testid="share-link-input"
                  className="flex-1 bg-ink border border-stroke px-3 py-2.5 font-mono text-xs text-acid focus:outline-none" />
                <button onClick={copy} data-testid="share-copy-button"
                  className="font-mono text-xs uppercase tracking-wider bg-elevated border border-stroke text-paper px-4 py-2.5 hover:border-acid flex items-center gap-2 justify-center">
                  {copied ? <Check size={14} className="text-acid" /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                </button>
                <a href={publicUrl} target="_blank" rel="noreferrer"
                  className="font-mono text-xs uppercase tracking-wider bg-acid text-ink font-bold px-4 py-2.5 flex items-center gap-2 justify-center">
                  <ExternalLink size={14} /> Open
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
